import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LucideIconService } from '../../services/lucide-icon.service';

@Component({
  selector: 'app-icon',
  template: `
    <div
      [class]="getIconClasses()"
      [style]="getIconStyles()"
      [attr.aria-label]="ariaLabel"
      [attr.role]="role"
      [innerHTML]="iconSvg">
    </div>
  `,
  styleUrls: ['./icon.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IconComponent implements OnInit, OnDestroy, OnChanges {
  @Input() name!: string;
  @Input() size?: 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | number;
  @Input() color?: string;
  @Input() weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  @Input() filled?: boolean;
  @Input() strokeWidth?: number;
  @Input() ariaLabel?: string;
  @Input() role?: string = 'img';

  iconSvg: SafeHtml = '';

  constructor(
    private lucideIconService: LucideIconService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadIcon();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['name'] && !changes['name'].firstChange) ||
        (changes['color'] && !changes['color'].firstChange) ||
        (changes['size'] && !changes['size'].firstChange) ||
        (changes['strokeWidth'] && !changes['strokeWidth'].firstChange) ||
        (changes['filled'] && !changes['filled'].firstChange)) {
      this.loadIcon();
    }

    // Forzar detección de cambios para asegurar re-renderizado
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    // Cleanup si es necesario
  }

  /**
   * Fuerza la recarga del icono
   */
  public reloadIcon(): void {
    this.loadIcon();
  }

  private async loadIcon(): Promise<void> {
    if (!this.name) return;

    try {
      const svgContent = await this.lucideIconService.loadIcon(this.name);
      const processedSvg = this.processSvg(svgContent);
      this.iconSvg = this.sanitizer.bypassSecurityTrustHtml(processedSvg);
      // Forzar detección de cambios después de cargar el icono
      this.cdr.markForCheck();
    } catch (error) {
      console.warn(`Error cargando icono ${this.name}:`, error);
      this.loadFallbackIcon();
      // Forzar detección de cambios después de cargar el fallback
      this.cdr.markForCheck();
    }
  }

  private processSvg(svgContent: string): string {
    const size = this.getIconSize();
    const strokeWidth = this.strokeWidth || 2;

    // Procesar el SVG para aplicar estilos
    let processedSvg = svgContent
      .replace(/width="[^"]*"/, `width="${size}"`)
      .replace(/height="[^"]*"/, `height="${size}"`)
      .replace(/stroke-width="[^"]*"/, `stroke-width="${strokeWidth}"`)
      .replace(/stroke="[^"]*"/, `stroke="currentColor"`)
      .replace(/fill="[^"]*"/, `fill="${this.filled ? 'currentColor' : 'none'}"`);

    // Agregar clases CSS
    const classes = this.getIconClasses();
    if (classes) {
      processedSvg = processedSvg.replace('<svg', `<svg class="${classes}"`);
    }

    return processedSvg;
  }

  private loadFallbackIcon(): void {
    // Icono de fallback simple
    const fallbackSvg = `
      <svg width="${this.getIconSize()}" height="${this.getIconSize()}" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="${this.strokeWidth || 2}"
           stroke-linecap="round" stroke-linejoin="round" class="${this.getIconClasses()}">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 6v6l4 2"/>
      </svg>
    `;
    this.iconSvg = this.sanitizer.bypassSecurityTrustHtml(fallbackSvg);
  }

  getIconSize(): number {
    if (typeof this.size === 'number') {
      return this.size;
    }
    const sizeMap = {
      'sm': 12,    // @font-size-xs
      'base': 14,  // @font-size-sm
      'lg': 16,    // @font-size-md
      'xl': 18,    // @font-size-lg
      '2xl': 20,   // @font-size-xl
      '3xl': 24,   // @font-size-2xl
      '4xl': 28    // @font-size-3xl
    };
    return sizeMap[this.size || 'base'] || 14;
  }

  getIconClasses(): string {
    const classes = ['lucide-icon'];

    if (this.size && typeof this.size === 'string') {
      classes.push(`icon-${this.size}`);
    }

    if (this.weight) {
      classes.push(`icon-${this.weight}`);
    }

    if (this.color) {
      const colorClass = this.color.replace('#', '');
      classes.push(`icon-color-${colorClass}`);
    }

    if (this.filled) {
      classes.push('icon-filled');
    }

    // Agregar clases de animación basadas en el nombre del icono
    if (this.name === 'loader' || this.name === 'loader-circle' || this.name === 'loader-2') {
      classes.push('lucide-spin-slow');
    }

    return classes.join(' ');
  }

  getIconStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    // Aplicar color al contenedor para que el SVG lo herede
    if (this.color) {
      styles['color'] = this.color;
    }

    if (this.weight) {
      const weightMap = {
        'light': '300',
        'normal': '400',
        'medium': '500',
        'semibold': '600',
        'bold': '700',
        'extrabold': '800'
      };
      styles['font-weight'] = weightMap[this.weight];
    }

    return styles;
  }
}

// Ejemplos de uso:
// Tamaños:
// <app-icon name="star" size="sm" color="#12DB88"></app-icon>    // 12px
// <app-icon name="star" size="base" color="#12DB88"></app-icon>  // 14px
// <app-icon name="star" size="lg" color="#12DB88"></app-icon>    // 16px
// <app-icon name="star" size="xl" color="#12DB88"></app-icon>    // 18px
// <app-icon name="star" size="2xl" color="#12DB88"></app-icon>   // 20px
// <app-icon name="star" size="3xl" color="#12DB88"></app-icon>   // 24px
// <app-icon name="star" size="4xl" color="#12DB88"></app-icon>   // 28px
// <app-icon name="star" [size]="14" color="#12DB88"></app-icon>  // Tamaño personalizado

// Tipos:
//   <app-icon name="home" size="lg" color="#12DB88"></app-icon>
//   <app-icon name="user" size="lg" color="#12DB88"></app-icon>
//   <app-icon name="settings" size="lg" color="#12DB88"></app-icon>
//   <app-icon name="search" size="lg" color="#12DB88"></app-icon>

// Loadings:
//   <app-icon name="loader-circle" size="xl" color="#12DB88"></app-icon>
//   <app-icon name="loader" size="xl" color="#12DB88"></app-icon>
